import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { BrainOrb, Card, ChipStrip, GlassPill } from '../components/primitives';
import { useActiveTeam } from '../lib/queries/useActiveTeam';
import { useTeamVideoTags, type TeamVideoTag } from '../lib/queries/useTeamVideoTags';
import './page-shell.css';
import './Clips.css';

function platformLabel(p: string | null): string {
  if (!p) return 'Clip';
  if (p === 'YOUTUBE') return 'YouTube';
  if (p === 'VEO') return 'Veo';
  if (p === 'HUDL') return 'Hudl';
  if (p === 'INTERNAL') return 'Internal';
  return p;
}

function clipTimeRange(t: TeamVideoTag): string | null {
  if (t.ts_in == null && t.ts_out == null) return null;
  const fmt = (s: number) => {
    const m = Math.floor(s / 60);
    const r = s % 60;
    return `${m}:${String(r).padStart(2, '0')}`;
  };
  if (t.ts_in != null && t.ts_out != null) return `${fmt(t.ts_in)} – ${fmt(t.ts_out)}`;
  if (t.ts_in != null) return `from ${fmt(t.ts_in)}`;
  return `until ${fmt(t.ts_out!)}`;
}

export function Clips() {
  const { team, loading: teamLoading } = useActiveTeam();
  const { rows, state, errorMessage } = useTeamVideoTags(team?.id);

  const [highlightsOnly, setHighlightsOnly] = useState(false);
  const [activePlatform, setActivePlatform] = useState<string | null>(null);

  const filtered = useMemo(() => {
    let list = rows;
    if (highlightsOnly) list = list.filter((r) => r.is_highlight);
    if (activePlatform) list = list.filter((r) => r.platform === activePlatform);
    return list;
  }, [rows, highlightsOnly, activePlatform]);

  const platformCounts = useMemo(() => {
    const map: Record<string, number> = {};
    for (const r of rows) if (r.platform) map[r.platform] = (map[r.platform] ?? 0) + 1;
    return map;
  }, [rows]);

  return (
    <main className="main page clips-page">
      <header className="page-head">
        <BrainOrb size="lg" ariaLabel="Brain" />
        <div>
          <span className="page-eyebrow">Layer 3 · clip library</span>
          <h1 className="page-title">Clips</h1>
          <p className="page-sub">
            Every video tag across your roster. Tag new ones from a player&apos;s profile.
          </p>
        </div>
      </header>

      {teamLoading || state === 'loading' ? (
        <p className="page-empty">Loading…</p>
      ) : null}
      {state === 'error' && (
        <Card variant="opaque">
          <p className="page-empty">
            Couldn&apos;t load clips.{errorMessage ? ` (${errorMessage})` : null}
          </p>
        </Card>
      )}
      {state === 'empty' && (
        <Card variant="opaque">
          <p className="page-empty">
            No clips yet. Open a <Link to="/players">player profile</Link> and tag one.
          </p>
        </Card>
      )}

      {state === 'ok' && (
        <>
          <ChipStrip
            chips={[
              { label: `all · ${rows.length}`, value: '__all', active: activePlatform === null },
              ...Object.entries(platformCounts).map(([p, count]) => ({
                label: `${platformLabel(p)} · ${count}`,
                value: p,
                active: activePlatform === p,
              })),
            ]}
            onSelect={(v) => setActivePlatform(v === '__all' ? null : v)}
          />
          <button
            type="button"
            className="clips-highlight-toggle"
            data-active={highlightsOnly || undefined}
            onClick={() => setHighlightsOnly((v) => !v)}
          >
            ⭐ Highlights only
          </button>

          {filtered.length === 0 && (
            <Card variant="opaque">
              <p className="page-empty">No clips match the active filters.</p>
            </Card>
          )}

          <ul className="clips-grid">
            {filtered.map((c) => (
              <li key={c.id}>
                <Card variant="opaque">
                  <div className="clip-card">
                    <div className="clip-card__head">
                      <a href={c.clip_url} target="_blank" rel="noopener noreferrer" className="clip-card__title">
                        {platformLabel(c.platform)}
                        {clipTimeRange(c) && <span className="clip-card__time"> · {clipTimeRange(c)}</span>}
                      </a>
                      <div className="clip-card__pills">
                        {c.is_highlight && <GlassPill mono>⭐ Highlight</GlassPill>}
                        {c.player_name && (
                          <Link to={`/players/${c.player_id}`}>
                            <GlassPill mono>{c.player_name}</GlassPill>
                          </Link>
                        )}
                      </div>
                    </div>
                    {c.notes && <p className="clip-card__notes">{c.notes}</p>}
                  </div>
                </Card>
              </li>
            ))}
          </ul>
        </>
      )}
    </main>
  );
}
